import '@babylonjs/core/Meshes/thinInstanceMesh';
import { gridOf, type Board, type Cell } from '@archon3d/core';
import { FresnelParameters } from '@babylonjs/core/Materials/fresnelParameters';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { BOARD_TOP_Y, COLORS, TILE_SIZE, TILE_THICKNESS } from './constants';
import { createBoardFrame } from './frame';
import { HEX_RADIUS, cellToWorld } from './layout';
import { PALETTES, type Palette } from './palette';
import { createWalls } from './walls';

export interface BoardTiles {
  /** All floor cells as thin instances of one box: one draw call for the whole board. */
  tiles: Mesh;
  material: StandardMaterial;
  /** A box under a square board, a six-sided prism under a hex board. */
  frame: Mesh;
  /** Every wall as a thin instance of one slab. */
  walls: Mesh;
  /** Glowing rings on the power points. */
  markers: Mesh;
  markerMaterial: StandardMaterial;
  /** Floor cells in thin-instance order, for picking. */
  floorCells: Cell[];
  /** Recolours the cycling squares for a luminosity phase (0 = darkest). */
  setLuminosityPhase(phase: number): void;
  /** Repaints every square in another palette's colours; the phase stays. */
  setPalette(palette: Palette): void;
  dispose(): void;
}

/** Gap between neighbouring tiles, as a fraction of the tile. */
const TILE_FILL = 0.965;

/** One tile to instance: a slab for squares, a six-sided prism with its points north–south for hexes. */
function createTileMesh(scene: Scene, board: Board): Mesh {
  if (gridOf(board) !== 'hex')
    return MeshBuilder.CreateBox(
      'tiles',
      { width: TILE_SIZE * TILE_FILL, height: TILE_THICKNESS, depth: TILE_SIZE * TILE_FILL },
      scene,
    );
  const tile = MeshBuilder.CreateCylinder(
    'tiles',
    { diameter: TILE_SIZE * TILE_FILL * 2 * HEX_RADIUS, height: TILE_THICKNESS, tessellation: 6 },
    scene,
  );
  // Babylon puts a prism's first corner on +X; a sixth of a turn makes the flat sides face
  // east and west so neighbours in a row meet edge to edge.
  tile.rotation.y = Math.PI / 6;
  tile.bakeCurrentTransformIntoVertices();
  return tile;
}

/** The floor tiles, the frame under them, the walls and the power-point rings of a board. */
export function createBoardTiles(
  scene: Scene,
  board: Board,
  parent: TransformNode | null = null,
): BoardTiles {
  const tiles = createTileMesh(scene, board);
  const floorCells = board.cells.filter((c) => c.kind === 'floor');
  const matrices = new Float32Array(floorCells.length * 16);
  const colors = new Float32Array(floorCells.length * 4);
  floorCells.forEach((cell, i) => {
    const p = cellToWorld(board, cell.x, cell.y);
    Matrix.Translation(p.x, BOARD_TOP_Y - TILE_THICKNESS / 2, p.z).copyToArray(matrices, i * 16);
  });
  tiles.thinInstanceSetBuffer('matrix', matrices, 16, true);
  tiles.thinInstanceSetBuffer('color', colors, 4, false);
  tiles.parent = parent;
  tiles.thinInstanceEnablePicking = true;
  tiles.isPickable = true;
  tiles.receiveShadows = true;

  let palette: Palette = PALETTES.classic;
  let phase = 2;
  const repaint = () => {
    const shades = palette.luminosityShades;
    const shade = Color3.FromHexString(shades[Math.max(0, Math.min(shades.length - 1, phase))]!);
    floorCells.forEach((cell, i) => {
      const color =
        cell.luminosity === 'cycling'
          ? shade
          : Color3.FromHexString(palette.tiles[cell.luminosity ?? 'cycling']);
      colors.set([color.r, color.g, color.b, 1], i * 4);
    });
    tiles.thinInstanceBufferUpdated('color');
  };
  const setLuminosityPhase = (next: number) => {
    phase = next;
    repaint();
  };
  repaint();

  const material = new StandardMaterial('tileMaterial', scene);
  material.diffuseColor = Color3.White();
  material.specularColor = new Color3(0.45, 0.45, 0.45);
  material.specularPower = 64;
  material.reflectionFresnelParameters = new FresnelParameters({
    bias: 0.15,
    power: 1.6,
    leftColor: Color3.White(),
    rightColor: new Color3(0.25, 0.25, 0.25),
  });
  tiles.material = material;

  const frame = createBoardFrame(scene, board, parent);
  const walls = createWalls(scene, board, parent);

  const markers = MeshBuilder.CreateTorus(
    'powerPoints',
    { diameter: TILE_SIZE * 0.66, thickness: TILE_SIZE * 0.07, tessellation: 28 },
    scene,
  );
  const powerCells = floorCells.filter((c) => c.powerPoint);
  const markerMatrices = new Float32Array(powerCells.length * 16);
  powerCells.forEach((cell, i) => {
    const p = cellToWorld(board, cell.x, cell.y);
    Matrix.Translation(p.x, BOARD_TOP_Y + 0.004, p.z).copyToArray(markerMatrices, i * 16);
  });
  markers.thinInstanceSetBuffer('matrix', markerMatrices, 16, true);
  const markerMaterial = new StandardMaterial('powerPointMaterial', scene);
  markerMaterial.disableLighting = true;
  markerMaterial.emissiveColor = Color3.FromHexString(COLORS.powerPoint);
  markers.material = markerMaterial;
  markers.parent = parent;
  markers.isPickable = false;

  const dispose = () => {
    tiles.dispose();
    frame.dispose();
    walls.dispose();
    markers.dispose();
    material.dispose();
    markerMaterial.dispose();
  };

  return {
    tiles,
    material,
    frame: frame.mesh,
    walls: walls.mesh,
    markers,
    markerMaterial,
    floorCells,
    setLuminosityPhase,
    setPalette(next) {
      palette = next;
      repaint();
    },
    dispose,
  };
}
